// Para todas las peticiones a la API, usar apiCall() o fetchAuthRetry() del api.service.js
// NUEVA ARQUITECTURA: Usar UserSessionRegistry como ÚNICA fuente (NO localStorage)
// Todas las llamadas ahora usan el método estándar apiCall() o fetchAuthRetry()

// Cache para evitar llamadas repetidas a la API
const userCache = new Map();
const avatarCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutos

/**
 * Limpiar cache expirado
 */
const cleanExpiredCache = (cache) => {
  const now = Date.now();
  for (const [key, value] of cache.entries()) {
    if (value.expiresAt < now) {
      cache.delete(key);
    }
  }
};

/**
 * Servicio para simular la interacción con usuarios (dado que no existen los endpoints reales)
 */
const UserService = {
  // Mock de datos de usuarios para simulación (como fallback)
  _mockUsers: [
    { 
      email: 'admin@bt4sap.com', 
      name: 'Administrador', 
      role_code: 'super_user', 
      organization_id: 1,
      active: true 
    },
    { 
      email: 'user@bt4sap.com', 
      name: 'Usuario Demo', 
      role_code: 'user', 
      organization_id: 1,
      active: true 
    }
  ],

  /**
   * Obtiene la lista de todos los usuarios
   * @param {number} organizationId Filtro opcional por organización
   * @returns {Promise<Array>} Lista de usuarios
   */
  getUsers: async (organizationId = null) => {
    try {
      // Importar apiCall para usar el método estándar
      const { apiCall } = await import('./api.service');
      
      // Construir endpoint con parámetros de consulta si se proporciona filtro
      const endpoint = organizationId 
        ? `users?organization_id=${organizationId}`
        : 'users';
          
      console.log(`[DEPURACIÓN] Obteniendo usuarios, endpoint: ${endpoint}`);
      
      // Usar apiCall para hacer la petición con autenticación
      const data = await apiCall(endpoint, 'GET', null, false, { forceWaitAuth: true });
      
      if (!data) {
        console.error('Respuesta vacía o inválida del servidor');
        throw new Error('Respuesta vacía o inválida del servidor');
      }
      
      console.log(`Usuarios obtenidos: ${data.length}`);
      return data;
    } catch (error) {
      console.error('Error obteniendo usuarios:', error);
      // No usar datos de fallback, propagar el error
      throw new Error(`Error obteniendo usuarios: ${error.message || 'Error desconocido'}`);
    }
  },
  
  /**
   * Obtiene un usuario específico por su email
   * @param {string} email Email del usuario
   * @param {boolean} forceRefresh Si es true, ignora el cache y fuerza la actualización
   * @returns {Promise<Object>} Datos del usuario
   */
  getUserByEmail: async (email, forceRefresh = false) => {
    try {
      // Limpiar cache expirado periódicamente
      cleanExpiredCache(userCache);
      
      // Verificar cache primero
      if (!forceRefresh) {
        const cached = userCache.get(email);
        if (cached && cached.expiresAt > Date.now()) {
          console.log(`[UserService] Usando cache para usuario ${email}`);
          return cached.data;
        }
      }
      
      // Importar apiCall para usar el método estándar
      const { apiCall } = await import('./api.service');
      
      // Intentar obtener del API
      try {
        console.log(`[DEPURACIÓN] Obteniendo usuario por email: ${email}`);
        const data = await apiCall(`users/${email}`, 'GET', null, false, { forceWaitAuth: true });
        console.log(`[DEPURACIÓN] Usuario obtenido:`, data);
        
        // Guardar en cache solo si la respuesta fue exitosa
        if (data) {
          userCache.set(email, {
            data,
            timestamp: Date.now(),
            expiresAt: Date.now() + CACHE_TTL
          });
        }
        
        return data;
      } catch (apiError) {
        console.warn(`Fallback: Usando usuario simulado para ${email}:`, apiError.message);
        
        // Simular respuesta
        const user = UserService._mockUsers.find(u => u.email === email);
        if (!user) {
          throw new Error('Usuario no encontrado');
        }
        
        // Cachear también el fallback
        userCache.set(email, {
          data: user,
          timestamp: Date.now(),
          expiresAt: Date.now() + CACHE_TTL
        });
        
        return user;
      }
    } catch (error) {
      console.error(`Error fetching user ${email}:`, error);
      throw error;
    }
  },
  
  /**
   * Crea un nuevo usuario
   * @param {Object} userData Datos del usuario a crear
   * @returns {Promise<Object>} Resultado de la operación
   */
  createUser: async (userData) => {
    try {
      console.log("Creando usuario con datos:", userData);
      
      const { apiCall } = await import('./api.service');
      const response = await apiCall('users', 'POST', userData);
      
      console.log("Respuesta de creación de usuario:", response);
      
      return response;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  },
  
  /**
   * Desactiva un usuario
   * @param {string} email Email del usuario a desactivar
   * @returns {Promise<Object>} Resultado de la operación
   */
  deactivateUser: async (email) => {
    try {
      const { apiCall } = await import('./api.service');
      return await apiCall(`users/${email}`, 'DELETE');
    } catch (error) {
      console.error(`Error deactivating user ${email}:`, error);
      throw error;
    }
  },

  /**
   * Activa un usuario desactivado
   * @param {string} email Email del usuario a activar
   * @returns {Promise<Object>} Resultado de la operación
   */
  activateUser: async (email) => {
    try {
      const { apiCall } = await import('./api.service');
      return await apiCall(`users/${email}/activate`, 'POST');
    } catch (error) {
      console.error(`Error activating user ${email}:`, error);
      throw error;
    }
  },
  
  /**
   * Actualiza un usuario existente
   * @param {string} email Email del usuario a actualizar
   * @param {Object} userData Datos del usuario
   * @returns {Promise<Object>} Resultado de la operación
   */
  updateUser: async (email, userData) => {
    try {
      console.log(`Actualizando usuario ${email} con datos:`, userData);
      
      const { apiCall } = await import('./api.service');
      const response = await apiCall(`users/${email}`, 'PUT', userData);
      
      console.log("Respuesta de actualización de usuario:", response);
      
      // Limpiar cache después de actualizar el usuario
      UserService.clearCache(email);
      
      return response;
    } catch (error) {
      console.error(`Error updating user ${email}:`, error);
      throw error;
    }
  },

  /**
   * Actualiza el perfil del usuario (name y extra_data)
   * @param {string} email Email del usuario
   * @param {Object} profileData Datos del perfil (name, avatar_url, etc.)
   * @returns {Promise<Object>} Resultado de la operación
   */
  updateProfile: async (email, profileData) => {
    try {
      console.log(`Actualizando perfil de usuario ${email}`);
      
      const { apiCall } = await import('./api.service');
      const response = await apiCall(`users/${email}`, 'PUT', profileData);
      
      console.log("Respuesta de actualización de perfil:", response);
      
      // Limpiar cache después de actualizar el perfil
      UserService.clearCache(email);
      
      return response;
    } catch (error) {
      console.error(`Error updating profile for user ${email}:`, error);
      throw error;
    }
  },

  /**
   * Sube una foto de perfil (avatar) para el usuario
   * @param {string} email Email del usuario
   * @param {File} file Archivo de imagen
   * @returns {Promise<Object>} Resultado con la URL del avatar
   */
  uploadAvatar: async (email, file) => {
    try {
      console.log(`Subiendo avatar para usuario ${email}`);
      console.log(`Archivo seleccionado:`, file.name, file.type, file.size);
      
      const formData = new FormData();
      formData.append('file', file);
      
      // Verificar que el FormData tiene el archivo
      console.log(`FormData creado, verificando contenido...`);
      for (let pair of formData.entries()) {
        console.log(`FormData entry:`, pair[0], pair[1]);
      }
      
      // Codificar el email para la URL (por si tiene caracteres especiales)
      const encodedEmail = encodeURIComponent(email);
      const endpoint = `users/${encodedEmail}/avatar`;
      console.log(`Endpoint de subida:`, endpoint);
      
      const { apiCall } = await import('./api.service');
      const response = await apiCall(endpoint, 'POST', formData, false, { isFormData: true });
      
      console.log("Respuesta de subida de avatar:", response);
      
      // Limpiar cache de avatar después de subir uno nuevo
      UserService.clearCache(email);
      
      return response;
    } catch (error) {
      console.error(`Error uploading avatar for user ${email}:`, error);
      throw error;
    }
  },

  /**
   * Obtiene el avatar del usuario como Object URL usando autenticación
   * @param {string} email Email del usuario
   * @param {boolean} forceRefresh Si es true, ignora el cache y fuerza la actualización
   * @returns {Promise<string|null>} URL de objeto para usar en <img src>, o null si no hay avatar
   */
  getAvatarObjectUrl: async (email, forceRefresh = false) => {
    try {
      console.log(`[Avatar] Obteniendo avatar como Object URL para usuario ${email}`);

      if (!email) {
        console.warn('[Avatar] Email no proporcionado al solicitar avatar');
        return null;
      }

      // Limpiar cache expirado periódicamente
      cleanExpiredCache(avatarCache);

      // Verificar cache primero
      if (!forceRefresh) {
        const cached = avatarCache.get(email);
        if (cached && cached.expiresAt > Date.now()) {
          console.log(`[Avatar] Usando cache para avatar de ${email}`);
          return cached.objectUrl;
        }
        
        // Si hay un Object URL anterior en cache expirado, revocarlo
        if (cached && cached.objectUrl) {
          try {
            URL.revokeObjectURL(cached.objectUrl);
          } catch (e) {
            // Ignorar errores al revocar URLs expiradas
          }
        }
      }

      const encodedEmail = encodeURIComponent(email);
      const endpoint = `users/${encodedEmail}/avatar`;

      // Para blob responses, necesitamos usar apiCallRoot o mantener fetch directo con UserSessionRegistry
      // apiCall() no maneja blob explícitamente, así que mantenemos fetch pero con UserSessionRegistry
      const { UserSessionRegistry } = await import('../stores/user-session.registry');
      const headers = await UserSessionRegistry.getAuthHeaders({});
      
      const { config } = await import('../config');
      const url = `${config.apiUrl}/api/${endpoint}`;

      const response = await fetch(url, {
        method: 'GET',
        headers,
        credentials: 'include',
      });

      if (!response.ok) {
        console.warn(`[Avatar] Error fetching avatar: ${response.status} ${response.statusText}`);
        // Cachear el resultado negativo (null) para evitar intentos repetidos
        avatarCache.set(email, {
          objectUrl: null,
          timestamp: Date.now(),
          expiresAt: Date.now() + (CACHE_TTL / 2) // Cache más corto para errores (2.5 min)
        });
        return null;
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      console.log('[Avatar] Object URL generado para avatar');
      
      // Guardar en cache solo si la respuesta fue exitosa
      avatarCache.set(email, {
        objectUrl,
        timestamp: Date.now(),
        expiresAt: Date.now() + CACHE_TTL
      });
      
      return objectUrl;
    } catch (error) {
      console.error(`Error obteniendo avatar para usuario ${email}:`, error);
      // Cachear el error para evitar intentos repetidos
      avatarCache.set(email, {
        objectUrl: null,
        timestamp: Date.now(),
        expiresAt: Date.now() + (CACHE_TTL / 2) // Cache más corto para errores (2.5 min)
      });
      return null;
    }
  },
  
  /**
   * Limpia el cache de usuarios y avatares
   * Útil cuando se actualiza información del usuario
   */
  clearCache: (email = null) => {
    if (email) {
      // Limpiar cache específico de un usuario
      const userCached = userCache.get(email);
      if (userCached) {
        userCache.delete(email);
      }
      
      const avatarCached = avatarCache.get(email);
      if (avatarCached && avatarCached.objectUrl) {
        try {
          URL.revokeObjectURL(avatarCached.objectUrl);
        } catch (e) {
          // Ignorar errores
        }
        avatarCache.delete(email);
      }
      console.log(`[UserService] Cache limpiado para usuario ${email}`);
    } else {
      // Limpiar todo el cache
      // Revocar todos los Object URLs antes de limpiar
      for (const [key, value] of avatarCache.entries()) {
        if (value.objectUrl) {
          try {
            URL.revokeObjectURL(value.objectUrl);
          } catch (e) {
            // Ignorar errores
          }
        }
      }
      userCache.clear();
      avatarCache.clear();
      console.log('[UserService] Todo el cache limpiado');
    }
  },
  
  /**
   * Hace público el avatar existente de un usuario
   * Útil para avatares que fueron subidos antes de implementar make_public=True
   * @param {string} email Email del usuario
   * @param {string} avatarUrl URL del avatar (opcional, si no se proporciona se busca en extra_data)
   * @returns {Promise<Object>} Resultado de la operación
   */
  makeAvatarPublic: async (email, avatarUrl = null) => {
    try {
      console.log(`Haciendo público el avatar para usuario ${email}`, avatarUrl ? `con URL: ${avatarUrl}` : '');
      
      const encodedEmail = encodeURIComponent(email);
      const endpoint = `users/${encodedEmail}/avatar/make-public`;
      
      const body = avatarUrl ? { avatar_url: avatarUrl } : {};
      
      const { apiCall } = await import('./api.service');
      const response = await apiCall(endpoint, 'POST', body);
      
      console.log("Respuesta de hacer público avatar:", response);
      return response;
    } catch (error) {
      console.error(`Error making avatar public for user ${email}:`, error);
      throw error;
    }
  },
  
  /**
   * Elimina/desactiva un usuario (mantiene compatibilidad con código existente)
   * @param {string} email Email del usuario a eliminar/desactivar
   * @returns {Promise<Object>} Resultado de la operación
   */
  deleteUser: async (email) => {
    try {
      console.log(`Desactivando usuario ${email} (método deleteUser)`);
      
      // Este método es un alias de deactivateUser por compatibilidad
      return await UserService.deactivateUser(email);
    } catch (error) {
      console.error(`Error deleting user ${email}:`, error);
      throw error;
    }
  },
  
  /**
   * Obtiene los módulos asignados a un usuario según su organización
   * @param {string} email Email del usuario
   * @param {number} specificOrgId ID específico de organización (opcional)
   * @returns {Promise<Array>} Lista de módulos filtrados
   */
  getUserModules: async (email, specificOrgId = null) => {
    try {
      console.log(`=== Obteniendo módulos para usuario: ${email} ===`);
      
      // Si se proporciona un ID específico, usarlo directamente
      if (specificOrgId) {
        console.log(`[DEPURACIÓN] Usando organización específica: ${specificOrgId}`);
      }
      
      // Obtener información actualizada de la organización del contexto actual
      const authData = JSON.parse(localStorage.getItem('authData') || '{}');
      const activeOrgData = JSON.parse(localStorage.getItem('activeOrganization') || '{}');
      
      // Priorizar el ID específico, luego la organización activa, y finalmente authData
      let organizationId = specificOrgId || Number(activeOrgData.organization_id || authData.organization_id || 0);
      
      // Asegurarnos de que el ID sea un número
      if (isNaN(organizationId)) {
        organizationId = 0;
      }
      
      const roleCode = authData.role_code || 'user';
      
      console.log(`[DEPURACIÓN] Contexto actual:`, {
        authData: JSON.stringify(authData),
        activeOrgData: JSON.stringify(activeOrgData),
        organizationId,
        roleCode
      });
      
      // Validar que el ID de organización sea válido
      if (!organizationId || isNaN(Number(organizationId)) || Number(organizationId) <= 0) {
        console.warn(`ID de organización inválido: ${organizationId}. No se mostrarán módulos.`);
        return [];
      }
      
      try {
        // Construir endpoint con el parámetro de organización
        const endpoint = `users/${email}/modules`;
        console.log(`[DEPURACIÓN] Endpoint para obtener módulos: ${endpoint}`);
        
        // Obtener módulos del usuario específicos para esta organización usando apiCall
        const { apiCall } = await import('./api.service');
        const modules = await apiCall(endpoint, 'GET', null, false, { 
          params: { organization_id: organizationId },
          forceWaitAuth: true 
        });
        console.log(`[DEPURACIÓN] Módulos obtenidos: ${modules ? modules.length : 0}`);
        
        return modules || [];
      } catch (error) {
        console.error(`Error al obtener módulos del usuario: ${error.message}`);
        return [];
      }
    } catch (error) {
      console.error(`Error crítico en getUserModules: ${error.message}`);
      return [];
    }
  },
  
  /**
   * Asigna un módulo a un usuario para una organización específica
   * @param {string} email Email del usuario
   * @param {number} moduleId ID del módulo
   * @param {number} organizationId ID de la organización
   * @returns {Promise<Object>} Resultado de la operación
   */
  assignModuleToUser: async (email, moduleId, organizationId) => {
    try {
      // Validar que se proporcione el ID de organización
      if (!organizationId || isNaN(Number(organizationId)) || Number(organizationId) <= 0) {
        throw new Error("ID de organización inválido. La asignación requiere una organización válida.");
      }
      
      const endpoint = `users/${email}/modules/${moduleId}`;
      console.log(`[DEPURACIÓN] Asignando módulo: ${endpoint}`);
      
      const { apiCall } = await import('./api.service');
      return await apiCall(endpoint, 'POST', null, false, { 
        params: { organization_id: organizationId } 
      });
    } catch (error) {
      console.error(`Error assigning module ${moduleId} to user ${email} for organization ${organizationId}:`, error);
      throw error;
    }
  },
  
  /**
   * Elimina un módulo asignado a un usuario para una organización específica
   * @param {string} email Email del usuario
   * @param {number} moduleId ID del módulo
   * @param {number} organizationId ID de la organización
   * @returns {Promise<Object>} Resultado de la operación
   */
  removeModuleFromUser: async (email, moduleId, organizationId) => {
    try {
      // Validar que se proporcione el ID de organización
      if (!organizationId || isNaN(Number(organizationId)) || Number(organizationId) <= 0) {
        throw new Error("ID de organización inválido. La eliminación requiere una organización válida.");
      }
      
      const endpoint = `users/${email}/modules/${moduleId}`;
      console.log(`[DEPURACIÓN] Eliminando módulo: ${endpoint}`);
      
      const { apiCall } = await import('./api.service');
      return await apiCall(endpoint, 'DELETE', null, false, { 
        params: { organization_id: organizationId } 
      });
    } catch (error) {
      console.error(`Error removing module ${moduleId} from user ${email} for organization ${organizationId}:`, error);
      throw error;
    }
  },
  
  /**
   * Obtiene los agentes asignados a un usuario
   * @param {string} email Email del usuario
   * @returns {Promise<Array>} Lista de agentes
   */
  getUserAgents: async (email) => {
    try {
      console.log(`[DEPURACIÓN] Obteniendo agentes para usuario: ${email}`);
      
      const { apiCall } = await import('./api.service');
      const agents = await apiCall(`users/${email}/agents`, 'GET');
      
      console.log(`[DEPURACIÓN] Agentes obtenidos: ${agents ? agents.length : 0}`);
      return agents || [];
    } catch (error) {
      console.error(`Error fetching agents for user ${email}:`, error);
      return []; // Retornar array vacío en caso de error
    }
  },
  
  /**
   * Asigna un agente a un usuario
   * @param {string} email Email del usuario
   * @param {number} agentId ID del agente
   * @returns {Promise<Object>} Resultado de la operación
   */
  assignAgentToUser: async (email, agentId) => {
    try {
      console.log(`[DEPURACIÓN] Asignando agente ${agentId} a usuario ${email}`);
      
      const { apiCall } = await import('./api.service');
      const result = await apiCall(`users/${email}/agents/${agentId}`, 'POST');
      
      console.log(`[DEPURACIÓN] Agente asignado exitosamente`);
      return result;
    } catch (error) {
      console.error(`Error assigning agent ${agentId} to user ${email}:`, error);
      throw error;
    }
  },
  
  /**
   * Elimina un agente asignado a un usuario
   * @param {string} email Email del usuario
   * @param {number} agentId ID del agente
   * @returns {Promise<Object>} Resultado de la operación
   */
  removeAgentFromUser: async (email, agentId) => {
    try {
      console.log(`[DEPURACIÓN] Eliminando agente ${agentId} de usuario ${email}`);
      
      const { apiCall } = await import('./api.service');
      const result = await apiCall(`users/${email}/agents/${agentId}`, 'DELETE');
      
      console.log(`[DEPURACIÓN] Agente eliminado exitosamente`);
      return result;
    } catch (error) {
      console.error(`Error removing agent ${agentId} from user ${email}:`, error);
      throw error;
    }
  },

  /**
   * Obtiene todas las organizaciones asignadas a un usuario
   * @param {string} email Email del usuario
   * @returns {Promise<Array>} Lista de organizaciones
   */
  getUserOrganizations: async (email) => {
    try {
      console.log(`[DEPURACIÓN] Obteniendo organizaciones para usuario: ${email}`);
      const { apiCall } = await import('./api.service');
      return await apiCall(`users/${email}/organizations`, 'GET', null, false, { 
        forceWaitAuth: true 
      });
    } catch (error) {
      console.error(`Error fetching organizations for user ${email}:`, error);
      throw error;
    }
  },

  /**
   * Obtiene las organizaciones donde el usuario tiene módulos asignados
   * Este método es útil para crear selectores de organización que muestren
   * solo las organizaciones relevantes para el usuario
   * @param {string} email Email del usuario
   * @returns {Promise<Array>} Lista de organizaciones donde el usuario tiene módulos asignados
   */
  getUserOrganizationsWithModules: async (email) => {
    try {
      console.log(`[DEPURACIÓN] Obteniendo organizaciones con módulos para usuario: ${email}`);
      const { apiCall } = await import('./api.service');
      return await apiCall(`users/${email}/organizations-with-modules`, 'GET', null, false, { 
        forceWaitAuth: true 
      });
    } catch (error) {
      console.error(`Error fetching organizations with modules for user ${email}:`, error);
      throw error;
    }
  },

  /**
   * Asigna un usuario existente a una organización
   * @param {Object} assignmentData Datos de la asignación (email, organization_id, role_id, is_primary)
   * @returns {Promise<Object>} Resultado de la operación
   */
  assignUserToOrganization: async (assignmentData) => {
    try {
      const { apiCall } = await import('./api.service');
      return await apiCall('users/assign-to-organization', 'POST', assignmentData);
    } catch (error) {
      console.error(`Error assigning user to organization:`, error);
      throw error;
    }
  },

  /**
   * Migra todos los usuarios a la nueva estructura de relaciones usuario-organización
   * Solo disponible para SuperUsuarios
   * @returns {Promise<Object>} Resultado de la operación
   */
  migrateUsersToUserOrganizations: async () => {
    try {
      const { apiCall } = await import('./api.service');
      return await apiCall('users/migrate-to-user-organizations', 'POST');
    } catch (error) {
      console.error(`Error migrating users to user_organizations:`, error);
      throw error;
    }
  },

  /**
   * Eliminar un usuario de una organización (quitar relación)
   * @param {string} email Email del usuario
   * @param {number} organizationId ID de la organización
   * @returns {Promise<Object>} Resultado de la operación
   */
  removeUserFromOrganization: async (email, organizationId) => {
    try {
      const { apiCall } = await import('./api.service');
      return await apiCall(`users/${email}/organizations/${organizationId}`, 'DELETE');
    } catch (error) {
      console.error(`Error removing user from organization:`, error);
      throw error;
    }
  },

  /**
   * Obtiene los usuarios de una organización específica
   * @param {number} organizationId - ID de la organización
   * @returns {Promise<Array>} Lista de usuarios de la organización
   */
  getUsersByOrganization: async (organizationId) => {
    try {
      // Importar la función apiCall para asegurar consistencia en las llamadas a la API
      const { apiCall } = await import('./api.service');
      
      // Verificar que tenemos un ID de organización válido
      if (!organizationId) {
        console.error("Se requiere organizationId para obtener usuarios");
        return [];
      }

      // Primero refrescar el contexto de autenticación para la organización
      await UserService.refreshOrganizationContext(organizationId);
      
      // Luego hacer la llamada para obtener usuarios
      return await apiCall(`users?organization_id=${organizationId}`);
    } catch (error) {
      console.error(`Error obteniendo usuarios para organización ${organizationId}:`, error);
      throw error;
    }
  },
  
  /**
   * Refresca el contexto de autenticación al cambiar de organización
   * Esto es crucial para mantener la autorización correcta al cambiar entre organizaciones
   * @param {number} organizationId - ID de la organización a la que se cambia
   */
  refreshOrganizationContext: async (organizationId) => {
    try {
      console.log(`===== INICIANDO CAMBIO DE CONTEXTO DE ORGANIZACIÓN: ${organizationId} =====`);
      
      // Convertir a número y verificar que el ID sea válido
      const orgId = Number(organizationId);
      if (isNaN(orgId) || orgId <= 0) {
        console.error(`Error: ID de organización inválido: ${organizationId}`);
        throw new Error("ID de organización inválido");
      }
      
      // Obtener los datos actuales
      const authData = JSON.parse(localStorage.getItem('authData') || '{}');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const currentOrgId = authData.organization_id;
      
      console.log(`[DEPURACIÓN] Cambiando organización: ${currentOrgId} -> ${orgId}`);
      
      // Crear headers con TODA la información necesaria
      const headers = {
        'Content-Type': 'application/json',
        'X-User-Role': authData.role_code || '',
        'X-User-Org': String(currentOrgId || ''),
        'X-User-Email': user.email || authData.email || ''
      };
      
      console.log(`[DEPURACIÓN] Headers de autenticación:`, {
        'X-User-Role': headers['X-User-Role'],
        'X-User-Org': headers['X-User-Org'],
        'X-User-Email': headers['X-User-Email']
      });
      
      // Usar fetchAuthRetry para manejar reintentos automáticos de autenticación
      console.log(`[DEPURACIÓN] Llamando a API para cambiar organización usando fetchAuthRetry...`);
      console.log(`[DEPURACIÓN] Body:`, JSON.stringify({ organization_id: orgId }));
      
      const { fetchAuthRetry } = await import('./api.service');
      const result = await fetchAuthRetry('auth/switch-organization', 'POST', { organization_id: orgId }, {
        forceWaitAuth: true
      });
      
      console.log(`[DEPURACIÓN] Respuesta del servidor:`, result);
      
      // Actualizar el contexto local con la nueva organización
      updateLocalContext(orgId, result);
      
      console.log(`===== CAMBIO DE CONTEXTO COMPLETADO CON ÉXITO =====`);
      return {
        success: true,
        message: "Contexto actualizado correctamente",
        organization_id: orgId,
        ...result
      };
    } catch (error) {
      console.error(`===== ERROR EN CAMBIO DE CONTEXTO =====`);
      console.error(error);
      
      // Intentar actualizar localmente a pesar del error
      try {
        console.log(`[DEPURACIÓN] Intentando actualización local como fallback...`);
        
        // Convertir a número
        const orgId = Number(organizationId);
        if (isNaN(orgId) || orgId <= 0) {
          throw new Error(`ID de organización inválido: ${organizationId}`);
        }
        
        // Actualizar localmente con función auxiliar
        updateLocalContext(orgId);
        
        console.log(`[DEPURACIÓN] Actualización local completada como fallback para organización ${orgId}`);
      } catch (localError) {
        console.error(`Error en actualización local:`, localError);
      }
      
      // Propagar el error para que el componente pueda manejarlo
      throw error;
    }
  },

  /**
   * Obtiene todos los módulos disponibles (solo para super_user)
   * @returns {Promise<Array>} Lista completa de módulos
   */
  getAllModules: async () => {
    try {
      const { apiCall } = await import('./api.service');
      
      console.log('[DEPURACIÓN] Obteniendo todos los módulos disponibles');
      
      const response = await apiCall('modules/all', 'GET', null, false, { 
        forceWaitAuth: true 
      });
      
      if (!Array.isArray(response)) {
        console.warn('[DEPURACIÓN] Respuesta no es un array:', response);
        return [];
      }
      
      console.log(`[DEPURACIÓN] Obtenidos ${response.length} módulos totales`);
      return response;
    } catch (error) {
      console.error('Error obteniendo todos los módulos:', error);
      
      // Devolver un array vacío
      return [];
    }
  },

  /**
   * Sincroniza el usuario actual con el backend
   * Esta función es utilizada para garantizar que el backend tiene la sesión correcta
   * @returns {Promise<boolean>} True si la sincronización fue exitosa
   */
  syncUserWithBackend: async () => {
    try {
      console.log("SINCRONIZACIÓN: Iniciando sincronización con backend...");
      
      // Usar forceSyncSession de api.service.js que maneja toda la lógica
      const { forceSyncSession } = await import('./api.service');
      const result = await forceSyncSession();
      
      if (result) {
        console.log("SINCRONIZACIÓN: Sincronización exitosa usando forceSyncSession");
        return true;
      } else {
        console.error("SINCRONIZACIÓN: forceSyncSession retornó false");
        return false;
      }
    } catch (error) {
      console.error("SINCRONIZACIÓN: Error en sincronización forzada:", error);
      return false;
    }
  },

  /**
   * Analiza el impacto de eliminar permanentemente un usuario
   * @param {string} email Email del usuario a analizar
   * @returns {Promise<Object>} Análisis detallado del impacto
   */
  analyzeUserDeletionImpact: async (email) => {
    try {
      console.log(`Analizando impacto de eliminación para usuario ${email}`);
      
      const { apiCall } = await import('./api.service');
      const result = await apiCall(`users/${email}/deletion-impact`, 'GET');
      
      console.log(`Análisis completado para usuario ${email}:`, result);
      return result;
    } catch (error) {
      console.error(`Error analyzing deletion impact for user ${email}:`, error);
      throw error;
    }
  },

  /**
   * Elimina permanentemente un usuario del sistema (solo SuperUsuarios)
   * ADVERTENCIA: Esta operación es irreversible
   * @param {string} email Email del usuario a eliminar permanentemente
   * @returns {Promise<Object>} Resultado de la operación
   */
  deleteUserPermanently: async (email) => {
    try {
      console.log(`Eliminando permanentemente usuario ${email}`);
      
      const { apiCall } = await import('./api.service');
      const result = await apiCall(`users/${email}/permanent`, 'DELETE');
      
      console.log(`Usuario ${email} eliminado permanentemente`);
      return result;
    } catch (error) {
      console.error(`Error permanently deleting user ${email}:`, error);
      throw error;
    }
  },

  /**
   * Reenvía el email de bienvenida/invitación a un usuario existente
   * @param {string} email Email del usuario
   * @param {Object} options Opciones adicionales (locale, product_name, support_email, logo_url, continue_url, template_id)
   * @returns {Promise<Object>} Resultado de la operación
   */
  resendInvitation: async (email, options = {}) => {
    try {
      console.log(`Reenviando invitación a usuario ${email}`);
      
      const { apiCall } = await import('./api.service');
      const result = await apiCall(`users/${email}/resend-invitation`, 'POST', options);
      
      console.log(`Invitación reenviada exitosamente a ${email}`);
      return result;
    } catch (error) {
      console.error(`Error resending invitation to user ${email}:`, error);
      throw error;
    }
  },

  inviteOnboarding: async ({ email, name, organization_id, skip_onboarding = false }) => {
    const { apiCall } = await import('./api.service');
    return apiCall('users/invite-onboarding', 'POST', { email, name, organization_id, skip_onboarding });
  },

  /**
   * Marca el onboarding como completado para un usuario específico.
   * Solo disponible para super_users.
   * 
   * @param {string} email Email del usuario a marcar como completado
   * @returns {Promise<Object>} Resultado de la operación
   */
  completeUserOnboarding: async (email) => {
    try {
      if (!email || !email.trim()) {
        throw new Error('Email es requerido');
      }

      // Importar apiCall para usar el método estándar
      const { apiCall } = await import('./api.service');
      
      const normalizedEmail = email.toLowerCase().trim();
      const endpoint = `users/${encodeURIComponent(normalizedEmail)}/onboarding/complete`;
      
      console.log(`[UserService] Completando onboarding para usuario: ${normalizedEmail}`);
      
      // Usar apiCall para hacer la petición con autenticación
      const data = await apiCall(endpoint, 'PUT', null, false, { forceWaitAuth: true });
      
      if (!data || !data.success) {
        throw new Error(data?.message || 'Error al completar onboarding');
      }
      
      console.log(`[UserService] Onboarding completado exitosamente para ${normalizedEmail}`);
      return data;
    } catch (error) {
      console.error(`[UserService] Error completando onboarding para ${email}:`, error);
      throw new Error(`Error completando onboarding: ${error.message || 'Error desconocido'}`);
    }
  },

  repairPermissions: async (email) => {
    const { apiCall } = await import('./api.service');
    return await apiCall(`users/${encodeURIComponent(email)}/repair-permissions`, 'POST');
  },

  repairPermissionsBulk: async () => {
    const { apiCall } = await import('./api.service');
    return await apiCall('users/repair-permissions/bulk', 'POST');
  },
};

/**
 * Actualiza el contexto local de la organización
 * Esta función auxiliar actualiza localStorage y cookies
 * @param {number} orgId - ID de la organización
 * @param {object} serverResult - Datos de respuesta del servidor (opcional)
 */
function updateLocalContext(orgId, serverResult = null) {
  console.log(`[CONTEXTO] Actualizando contexto local para organización ${orgId}`);
  
  try {
    // Actualizar usuario
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    if (currentUser && currentUser.email) {
      currentUser.organization_id = orgId;
      
      // Si hay datos del servidor, actualizar también organization_name
      if (serverResult && serverResult.organization_name) {
        currentUser.organization_name = serverResult.organization_name;
      }
      
      localStorage.setItem('user', JSON.stringify(currentUser));
      console.log(`[CONTEXTO] Usuario actualizado en localStorage`);
    }
    
    // Actualizar authData para las cabeceras
    const authData = JSON.parse(localStorage.getItem('authData') || '{}');
    authData.organization_id = orgId;
    
    // Actualizar otros campos si están disponibles en la respuesta del servidor
    if (serverResult) {
      if (serverResult.organization_name) {
        authData.organization_name = serverResult.organization_name;
      }
      
      if (serverResult.role_code) {
        authData.role_code = serverResult.role_code;
      }
    }
    
    localStorage.setItem('authData', JSON.stringify(authData));
    console.log(`[CONTEXTO] AuthData actualizado en localStorage:`, authData);
    
    // Actualizar cookie de contexto
    const cookieDate = new Date();
    cookieDate.setTime(cookieDate.getTime() + (24 * 60 * 60 * 1000)); // 1 día
    const expires = `expires=${cookieDate.toUTCString()}`;
    document.cookie = `org_context=${orgId}; path=/; ${expires}; samesite=lax`;
    console.log(`[CONTEXTO] Cookie de contexto actualizada`);
    
    // Actualizar activeOrganization si existe
    try {
      const activeOrg = JSON.parse(localStorage.getItem('activeOrganization') || '{}');
      if (activeOrg) {
        activeOrg.organization_id = orgId;
        
        // Actualizar otros campos si están disponibles
        if (serverResult && serverResult.organization_name) {
          activeOrg.organization_name = serverResult.organization_name;
        }
        
        if (serverResult && serverResult.role_code) {
          activeOrg.role_code = serverResult.role_code;
        }
        
        localStorage.setItem('activeOrganization', JSON.stringify(activeOrg));
        console.log(`[CONTEXTO] activeOrganization actualizado en localStorage`);
      }
    } catch (activeOrgError) {
      console.warn(`[CONTEXTO] Error actualizando activeOrganization:`, activeOrgError);
    }
    } catch (error) {
      console.error(`[CONTEXTO] Error actualizando contexto local:`, error);
      throw error;
    }
  }


export default UserService;